import { Singleton } from 'typescript-ioc/es5'
import aqe from '@pushly/aqe'
import { AbstractRequestService } from '@pushly/aqe/lib/services/abstract-request-service'
import { HttpMethod } from '@pushly/aqe/lib/enums'
import { IApiResponse } from '@pushly/aqe/lib/interfaces'
import type { SegmentQueryField } from '@pushly/models/lib/interfaces/segment-query-field'

interface CacheEntry {
    fields: SegmentQueryField[]
    expiresAt: number
}

/**
 * Loads the catalog of fields available to the segment query builder.
 *
 * Target API paths (sailfish DomainSegmentController / AccountSegmentsController):
 *   GET /domains/:domainId/segments/fields
 *   GET /accounts/:accountId/segments/fields
 *
 * The catalog is stable for the lifetime of a page visit but expensive to
 * rebuild server-side, so successful responses are cached per level+id for a
 * short TTL. Failures are never cached — a transient outage must not disable
 * the builder for the rest of the session.
 */
@Singleton
export class SegmentQueryFieldsService extends AbstractRequestService {
    private static readonly FIELDS_CACHE_TTL_MS = 5 * 60 * 1000

    /** Keys carry the level so a domain id and an account id of equal value stay distinct. */
    private readonly fieldsCache = new Map<string, CacheEntry>()

    public constructor() {
        super()
    }

    public async getDomainFieldsCached(domainId: number): Promise<SegmentQueryField[]> {
        return this.getCached(`domain:${domainId}`, () => this.getDomainFields(domainId))
    }

    public async getAccountFieldsCached(accountId: number): Promise<SegmentQueryField[]> {
        return this.getCached(`account:${accountId}`, () => this.getAccountFields(accountId))
    }

    /**
     * Returns the SegmentQueryField descriptors available to a single domain.
     *
     * Case conversion is skipped because the endpoint already emits the descriptors
     * verbatim; converting would rename their metadata keys and break field filtering.
     */
    public async getDomainFields(domainId: number): Promise<IApiResponse<SegmentQueryField[]>> {
        return this.request(
            HttpMethod.GET,
            `${aqe.defaults.publicApiDomain}/domains/${domainId}/segments/fields`,
            {
                config: { skipCaseConversion: true },
            },
        )
    }

    /** Returns the SegmentQueryField descriptors available across an account's domains. */
    public async getAccountFields(accountId: number): Promise<IApiResponse<SegmentQueryField[]>> {
        return this.request(
            HttpMethod.GET,
            `${aqe.defaults.publicApiDomain}/accounts/${accountId}/segments/fields`,
            {
                config: { skipCaseConversion: true },
            },
        )
    }

    private async getCached(
        key: string,
        fetch: () => Promise<IApiResponse<SegmentQueryField[]>>,
    ): Promise<SegmentQueryField[]> {
        const cached = this.fieldsCache.get(key)
        if (cached && Date.now() < cached.expiresAt) {
            return cached.fields
        }

        let fields: SegmentQueryField[] = []
        try {
            const response = await fetch()
            if (response.ok && Array.isArray(response.data)) {
                fields = response.data
                this.fieldsCache.set(key, {
                    fields,
                    expiresAt: Date.now() + SegmentQueryFieldsService.FIELDS_CACHE_TTL_MS,
                })
            }
        } catch {
            // Callers render an error affordance; the builder must not crash.
        }

        return fields
    }
}
